import { supabase } from "../supabase/SupabaseClient";
import { dominioPoliza } from "../shared/seguroFicha";

/* Las pólizas de seguro de la flota, para el sector "Seguros" del Hub.
 *
 * Los datos viven en `polizas_seguro` (una fila por unidad, con la carátula en
 * `pdf_b64`) y se leen por la vista `poliza_hub`, que ya les pega el interno del
 * padrón. La vista es `security_invoker`: aplica la RLS de `polizas_seguro`,
 * que deja leer a `ver.polizas` o a Satelital (`cargar.satelital`) —la misma
 * regla que el menú, `PERMISO_POLIZAS`—. A quien no la tiene le devuelve CERO
 * filas, no un error: por eso la ficha de la unidad ni la pide sin el permiso
 * (si la pidiera, diría «no hay póliza cargada» de una que sí está).
 *
 * El mismo bot de WhatsApp responde `seguro <patente|interno>` con esta misma
 * hoja; esto es la ventana para mirarlas todas juntas y ver los vencimientos.
 */

/* El listado, sin el PDF: la carátula pesa ~95 KB y son 30+; traerlas todas de
 * una sería megas al pedo. El `pdf_b64` se pide por unidad al abrir una. */
export async function lista() {
  const { data, error } = await supabase
    .from("poliza_hub")
    .select("dominio, interno, tipo, marca, poliza_nro, vence, actualizado_en, estado")
    .order("vence", { ascending: true });
  if (error) {
    console.warn("[seguros] no pude leer las pólizas:", error.message);
    return { filas: [], error: error.message };
  }
  return { filas: data || [], error: "" };
}

/* La póliza de una unidad, por dominio: número y vencimiento, SIN la carátula
 * (`pdf_b64` pesa ~95 KB y la ficha no la muestra). Es lo que necesitan la
 * línea «Seguro», la línea de tiempo y el aviso de arriba de la ficha.
 *
 * El dominio se compara normalizado (`dominioPoliza`): `polizas_seguro` lo
 * guarda en mayúsculas y sin separadores, y en el padrón hay chapas tipeadas
 * con espacios o guiones (11 tractores) que comparadas en crudo no encontraban
 * su póliza.
 *
 * Devuelve `{ dominio, poliza_nro, vence, estado }` o `null` si no hay póliza
 * cargada. Las que Seguros dio de baja vienen con `estado` «baja».
 * Si no se puede leer, LANZA: «no pude leer» no es «no hay», y la ficha calla
 * la línea en vez de decir algo que no sabe. */
export async function polizaDeUnidad(dominio) {
  const d = dominioPoliza(dominio);
  if (!d) return null;
  const { data, error } = await supabase
    .from("poliza_hub")
    .select("dominio, poliza_nro, vence, estado")
    .eq("dominio", d)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/* La carátula de una unidad, lista para abrir en el navegador. Devuelve un
 * object URL (o null). El que llama tiene que hacer `URL.revokeObjectURL` cuando
 * termina, para no dejar el blob colgado. */
export async function caratula(dominio) {
  if (!dominio) return { url: null, error: "Falta la unidad." };
  const { data, error } = await supabase
    .from("poliza_hub")
    .select("pdf_b64")
    .eq("dominio", dominio)
    .maybeSingle();
  if (error) {
    console.warn("[seguros] no pude leer la carátula:", error.message);
    // El detalle queda en la consola: a la pantalla va lo de siempre.
    return { url: null, error: "No se pudo consultar." };
  }
  const b64 = data?.pdf_b64 || "";
  if (!b64) return { url: null, error: "Esa unidad no tiene la carátula cargada." };
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    return { url, error: "" };
  } catch (e) {
    return { url: null, error: "No se pudo abrir la carátula." };
  }
}

/* Cómo está el vencimiento, para el semáforo. Sin fecha no se inventa un estado.
 * «Por vencer» son los próximos 30 días —el pedido de Leonel era avisar con 5,
 * pero para mirar de un vistazo un mes es más útil—. */
export function estadoVencimiento(vence, hoy = new Date()) {
  if (!vence) return { estado: "sin_fecha", dias: null };
  const v = new Date(`${vence}T00:00:00`);
  if (Number.isNaN(v.getTime())) return { estado: "sin_fecha", dias: null };
  const dias = Math.round((v - new Date(hoy.toDateString())) / 86400000);
  if (dias < 0) return { estado: "vencida", dias };
  if (dias <= 30) return { estado: "por_vencer", dias };
  return { estado: "vigente", dias };
}
